import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, Heart, Box, Sparkles, ArrowRight, Instagram } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/analytics";
import ProductCard from "../components/ProductCard";
const HERO = "https://static.prod-images.emergentagent.com/jobs/3af35290-5c18-4b44-9040-8af3e70333b3/images/dc2769bb6a3090ae603b71d40e6283176952370b7879a4d82c6bb73a25c50ec3.png";
const STORY = "https://static.prod-images.emergentagent.com/jobs/3af35290-5c18-4b44-9040-8af3e70333b3/images/854a1d4cf67b4543f9bb7c8f9ef61347034ef049f842bf92270c88e7ba9193bf.png";

const TRUST = [
    { icon: Leaf,    title: "Soft & Safe",        sub: "Gentle on delicate skin" },
    { icon: Sparkles,title: "Premium Fabrics",    sub: "Breathable & comfortable" },
    { icon: Heart,   title: "Thoughtful Design",  sub: "Made for play & all-day wear" },
    { icon: Box,     title: "Easy Returns",       sub: "Hassle-free & simple" },
];

export default function Home() {
    const [cats, setCats] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [insta, setInsta] = useState([]);

    useEffect(() => {
        document.title = "Loomara — Joyfully designed from the first crawl";
        api.get("/categories").then(({ data }) => setCats(data));
        api.get("/products", { params: { featured: true, limit: 8 } }).then(({ data }) => {
            setFeatured(data);
            track("view_item_list", { list_name: "home_featured", items: data.slice(0,8).map((p,i)=>({item_id:p.id, item_name:p.name, item_category:p.category, price:p.price, index:i}))});
        });
        api.get("/products", { params: { limit: 8 } }).then(({ data }) => setInsta(data));
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="container-boutique py-12 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-5 relative z-10 animate-fade-up">
                        <p className="eyebrow">A Boutique for Little Ones</p>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-ink-900 leading-[1.05] mt-5">
                            Little moments.
                            <br/>
                            <span className="italic text-sage-600">Beautiful beginnings.</span>
                        </h1>
                        <p className="mt-6 text-ink-700 text-base sm:text-lg leading-relaxed max-w-md">
                            Thoughtfully made outfits for your little one's every adventure — softness you can feel, details you'll keep forever.
                        </p>
                        <div className="mt-8 flex items-center gap-4">
                            <Link to="/shop" className="btn-primary" data-testid="hero-shop-now-btn">
                                Shop Now <ArrowRight size={14} />
                            </Link>
                            <Link to="/about" className="text-xs uppercase tracking-[0.22em] text-sage-700 hover:text-sage-600 border-b border-sage-300/60 pb-1" data-testid="hero-our-story-link">
                                Our Story
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-7 relative">
                        <div className="relative aspect-[4/3] rounded-[2rem] overflow-hidden bg-ivory-100 shadow-[0_20px_60px_rgba(86,100,75,0.12)]">
                            <img src={HERO} alt="Loomara baby in peach dress" className="absolute inset-0 w-full h-full object-cover" />
                        </div>
                        <div className="absolute -top-3 right-6 w-14 h-14 rounded-full bg-blush-200/60 -z-10 blur-sm" />
                    </div>
                </div>

                {/* soft scallop divider */}
                <svg viewBox="0 0 1440 40" className="w-full -mt-6" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M0,20 C240,40 480,0 720,20 C960,40 1200,0 1440,20 L1440,40 L0,40 Z" fill="#FFFDF8" />
                </svg>
            </section>

            {/* TRUST STRIP */}
            <section className="bg-ivory-50 py-10 border-y border-sage-300/20">
                <div className="container-boutique grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="trust-strip">
                    {TRUST.map((t) => (
                        <div key={t.title} className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-full border border-sage-300/50 flex items-center justify-center text-sage-600 shrink-0">
                                <t.icon size={18} strokeWidth={1.4} />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-[0.18em] text-ink-900 font-medium">{t.title}</p>
                                <p className="text-xs text-ink-500 mt-1">{t.sub}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* SHOP BY CATEGORY */}
            <section className="container-boutique py-16 lg:py-24" data-testid="category-section">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <p className="eyebrow">Explore Loomara</p>
                        <h2 className="font-serif text-4xl sm:text-5xl text-ink-900 mt-3">Shop by Category</h2>
                    </div>
                    <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-sage-700 hover:text-sage-600">
                        View All <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
                    {cats.map((c) => (
                        <Link
                            key={c.id}
                            to={`/category/${c.slug}`}
                            className="card-soft overflow-hidden block group"
                            data-testid={`category-card-${c.slug}`}
                        >
                            <div className="relative aspect-square overflow-hidden bg-ivory-100">
                                <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover editorial-img" loading="lazy" />
                            </div>
                            <div className="text-center py-4 bg-white">
                                <span className="text-[11px] uppercase tracking-[0.28em] text-ink-900">{c.name}</span>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* STORY BANNER */}
            <section className="relative bg-sage-100 py-16 lg:py-24" data-testid="story-banner">
                <div className="container-boutique grid lg:grid-cols-2 gap-10 items-center">
                    <div className="relative animate-fade-up">
                        <p className="eyebrow">Our Story</p>
                        <h2 className="font-serif text-4xl sm:text-5xl text-ink-900 mt-3 leading-[1.05]">
                            Made with <span className="italic text-sage-600">love</span>,<br/>
                            for little adventures.
                        </h2>
                        <p className="mt-6 text-ink-700 max-w-md leading-relaxed">
                            At Loomara, every piece is crafted with comfort, care, and a touch of magic — because childhood is meant to be joyful, and the moments worth remembering are the smallest ones.
                        </p>
                        <Link to="/about" className="btn-primary mt-8" data-testid="story-our-story-btn">
                            Our Story
                        </Link>
                    </div>
                    <div className="relative">
                        <div className="aspect-[4/5] sm:aspect-[5/4] rounded-[2rem] overflow-hidden">
                            <img src={STORY} alt="Pink dress and hat" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURED PRODUCTS */}
            <section className="container-boutique py-16 lg:py-24" data-testid="featured-section">
                <div className="text-center mb-12">
                    <p className="eyebrow">Loved by Little Ones</p>
                    <h2 className="font-serif text-4xl sm:text-5xl text-ink-900 mt-3">Boutique Favourites</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    {featured.map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} listName="home_featured" />
                    ))}
                </div>
                <div className="text-center mt-12">
                    <Link to="/shop" className="btn-secondary" data-testid="featured-view-all-btn">View All Products</Link>
                </div>
            </section>

            {/* INSTAGRAM STRIP */}
            <section className="bg-ivory-100 py-16" data-testid="instagram-section">
                <div className="text-center mb-8">
                    <h3 className="font-serif text-3xl text-ink-900">Little looks, big smiles</h3>
                    <a href="https://instagram.com/loomara.kids" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-sage-700 mt-3 hover:text-sage-600">
                        <Instagram size={14} strokeWidth={1.5} /> Follow us @loomara.kids
                    </a>
                </div>
                <div className="container-boutique grid grid-cols-3 md:grid-cols-6 gap-3">
                    {insta.slice(0, 6).map((p) => (
                        <Link key={p.id} to={`/product/${p.slug}`} className="aspect-square rounded-xl overflow-hidden bg-white block">
                            <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover editorial-img" loading="lazy" />
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
